import { Fragment, useState } from 'react';
import { jobsApi, type JobFile, type JobRow, type JobRun, type RunResult } from '../jobsApi';
import { useLoad } from '../lib';

// Scheduled Jobs — the Intellam crons ported to commerce (server/src/jobs/). Each job: its schedule
// (facility time, Adobe's own expression), whether it is on, its last run's outcome and error, and
// its history. A dry run reads and computes everything and sends and writes nothing, so staff can
// check a job against Adobe's before turning it on. Every job ships OFF: Adobe still sends these
// same emails, and a job turned on before cutover would double-send.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_NAMES: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
const hhmm = (h: string, m: string) => `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;

/** A cron expression in words, for the shapes Intellam uses; anything else is shown as-is. */
export function describeCron(expr: string): string {
  const [m, h, dom, mon, dow] = expr.trim().split(/\s+/);
  if (!m || !h || dom !== '*' || mon !== '*') return expr;
  const days = dow === '*' ? 'Daily' : dow!.split(',').map((d) => DOW[DOW_NAMES[d.toUpperCase()] ?? Number(d) % 7] ?? d).join(', ');
  if (m === '*' && h === '*') return 'Every minute';
  if (/^\*\/\d+$/.test(m) && h === '*') return `Every ${m.slice(2)} min`;
  if (/^\d+$/.test(m) && h === '*') return `Hourly at :${m.padStart(2, '0')}`;
  if (/^\d+$/.test(m) && /^\d+-\d+$/.test(h)) { const [a, b] = h.split('-'); return `${days}, hourly ${hhmm(a!, m)}–${hhmm(b!, m)}`; }
  if (/^\d+$/.test(m) && /^[\d,]+$/.test(h)) return `${days} ${h.split(',').map((x) => hhmm(x, m)).join(', ')}`;
  return expr;
}

/** An instant on the facility clock — the clock every schedule here is read on. */
const et = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const OUTCOME_LABEL: Record<string, string> = { ok: 'OK', skipped: 'Skipped', error: 'Error', running: 'Running' };
function Outcome({ o }: { o: string | null }) {
  if (!o) return <span className="muted">Never run</span>;
  return <span className={`job-outcome ${o}`}>{OUTCOME_LABEL[o] ?? o}</span>;
}

/** The summary's headline numbers, in a line — the full JSON is in the history. */
function summaryLine(s: Record<string, unknown> | null | undefined): string {
  if (!s) return '';
  return Object.entries(s)
    .filter(([, v]) => typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean')
    .slice(0, 5).map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${String(v)}`).join(' · ');
}

function History({ name }: { name: string }) {
  const { data, err } = useLoad(() => jobsApi.runs(name), [name]);
  if (err) return <p className="msg bad">⚠ {err}</p>;
  if (!data) return <p className="loading">Loading…</p>;
  if (data.length === 0) return <p className="muted">No runs yet.</p>;
  return (
    <table className="job-runs">
      <thead><tr><th>#</th><th>Started</th><th>Trigger</th><th>Outcome</th><th>Summary</th></tr></thead>
      <tbody>
        {data.map((r: JobRun) => (
          <tr key={r.id}>
            <td className="mono">{r.id}</td>
            <td className="mono">{et(r.started_at)}</td>
            <td>{r.trigger === 'schedule' ? `Schedule ${r.slot ?? ''}` : `Run-now · ${r.triggered_by ?? '—'}`}{r.dry_run && <span className="pill dry">dry run</span>}</td>
            <td><Outcome o={r.outcome} />{r.error && <div className="job-error">{r.error}</div>}</td>
            <td><pre className="job-summary">{JSON.stringify(r.summary ?? {}, null, 1)}</pre></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const size = (b: number) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

/** The files a job keeps (Adobe wrote them to its own disk) — shown only for a job that has kept one. */
function Files({ name }: { name: string }) {
  const { data } = useLoad(() => jobsApi.files(name), [name]);
  if (!data?.length) return null;
  return (
    <div className="job-files">
      <h3>Files kept</h3>
      <table className="job-runs">
        <thead><tr><th>File</th><th>Written</th><th className="num">Size</th><th></th></tr></thead>
        <tbody>
          {data.map((f: JobFile) => (
            <tr key={f.id}>
              <td className="mono">{f.filename}</td><td className="mono">{et(f.created_at)}</td><td className="num">{size(f.size)}</td>
              <td><a className="btn-sm ghost" href={jobsApi.fileUrl(name, f.id)} download>Download</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ScheduledJobsPanel({ canRun }: { canRun: boolean }) {
  const { data, err, reload } = useLoad(() => jobsApi.jobs());
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ name: string; text: string; bad: boolean } | null>(null);

  async function run(job: JobRow, dryRun: boolean) {
    if (!dryRun && !window.confirm(`Run ${job.title} for real now? ${job.effect}`)) return;
    setBusy(job.name); setMsg(null);
    try {
      const r: RunResult = await jobsApi.run(job.name, dryRun);
      const text = r.running ? 'Still running — check its history in a moment.'
        : `${dryRun ? 'Dry run' : 'Run'} ${OUTCOME_LABEL[r.outcome ?? ''] ?? r.outcome}${r.error ? `: ${r.error}` : ''}${summaryLine(r.summary) ? ` — ${summaryLine(r.summary)}` : ''}`;
      setMsg({ name: job.name, text, bad: r.outcome === 'error' });
      setOpen(job.name);
      reload();
    } catch (e) {
      setMsg({ name: job.name, text: (e as Error).message, bad: true });
    } finally { setBusy(null); }
  }

  async function toggle(job: JobRow) {
    if (!job.enabled && !window.confirm(
      `Turn ON ${job.title}? Adobe still runs ${job.name} — while Adobe is live, both will send. Only turn this on at cutover.`)) return;
    setBusy(job.name); setMsg(null);
    try { await jobsApi.setEnabled(job.name, !job.enabled); reload(); }
    catch (e) { setMsg({ name: job.name, text: (e as Error).message, bad: true }); }
    finally { setBusy(null); }
  }

  const groups = data ? [...new Set(data.jobs.map((j) => j.group))] : [];
  const on = data?.jobs.filter((j) => j.enabled).length ?? 0;

  return (
    <div className="stack">
      <h1>Scheduled Jobs</h1>
      <p className="sub">Adobe's Intellam crons, ported. Every job ships off — Adobe still sends these emails, so turn one on only at cutover. A dry run sends and changes nothing.</p>
      {err && <p className="msg bad">⚠ {err}</p>}
      {data && (
        <div className="cards">
          <div className="stat"><div className="stat-k">Runner</div><div className="stat-v">{data.schedulerOn ? 'On' : 'Off'}</div>
            <div className="stat-sub">{data.schedulerOn ? 'SCHEDULED_JOBS=1 here' : 'SCHEDULED_JOBS unset: dry runs only'}</div></div>
          <div className="stat"><div className="stat-k">Jobs enabled</div><div className="stat-v">{on} / {data.jobs.length}</div>
            <div className="stat-sub">switched per job, below</div></div>
          <div className="stat"><div className="stat-k">Email</div><div className="stat-v">{data.emailReady ? 'Ready' : 'Off'}</div>
            <div className="stat-sub">{data.emailReady ? 'Resend, as purchaseorders@tavezio.com' : 'RESEND_API_KEY unset: sends are no-ops'}</div></div>
        </div>
      )}
      {data && groups.map((g) => (
        <section className="card" key={g}>
          <h2>{g}</h2>
          <table className="jobs">
            <thead><tr><th>Job</th><th>Schedule (ET)</th><th>Last run</th><th>State</th><th></th></tr></thead>
            <tbody>
              {data.jobs.filter((j) => j.group === g).map((job) => (
                <Fragment key={job.name}>
                  <tr>
                    <td className="job-cell">
                      <button className="link job-title" onClick={() => setOpen(open === job.name ? null : job.name)}>{open === job.name ? '▾' : '▸'} {job.title}</button>
                      <div className="mono">{job.name}</div>
                      <div className="muted job-effect">{job.effect}</div>
                      {job.missingEnv.length > 0 && <div className="job-missing">Needs {job.missingEnv.join(', ')}</div>}
                    </td>
                    <td><div>{describeCron(job.schedule)}</div><div className="mono">{job.schedule}</div>
                      {job.enabled && job.nextRun && <div className="muted job-next">next {et(job.nextRun)}</div>}</td>
                    <td><Outcome o={job.lastOutcome} />{job.lastStartedAt && <div className="mono">{et(job.lastStartedAt)}</div>}
                      {job.lastError && <div className="job-error">{job.lastError}</div>}
                      {!job.lastError && summaryLine(job.lastSummary) && <div className="muted job-effect">{summaryLine(job.lastSummary)}</div>}</td>
                    <td><span className={`pill ${job.enabled ? 'job-on' : 'job-off'}`}>{job.enabled ? 'Enabled' : 'Disabled'}</span>
                      {job.updatedBy && <div className="muted job-effect">by {job.updatedBy}</div>}</td>
                    <td>
                      <div className="actions job-actions">
                        <button className="btn-sm ghost" disabled={busy === job.name} onClick={() => run(job, true)}>{busy === job.name ? '…' : 'Dry run'}</button>
                        {canRun && <button className="btn-sm" disabled={busy === job.name || !job.enabled || !data.schedulerOn}
                          title={!data.schedulerOn ? 'The runner is off on this environment' : !job.enabled ? 'Enable the job first' : 'Run it now, for real'}
                          onClick={() => run(job, false)}>Run now</button>}
                        {canRun && <button className="btn-sm ghost" disabled={busy === job.name} onClick={() => toggle(job)}>{job.enabled ? 'Disable' : 'Enable'}</button>}
                      </div>
                    </td>
                  </tr>
                  {msg?.name === job.name && <tr><td colSpan={5}><p className={`msg ${msg.bad ? 'bad' : 'good'}`}>{msg.text}</p></td></tr>}
                  {open === job.name && (
                    <tr className="job-detail"><td colSpan={5}>
                      <p className="job-desc">{job.description}</p>
                      <p className="muted job-effect">Ports <span className="mono">{job.source}</span>{job.requires.length ? <> · needs <span className="mono">{job.requires.join(', ')}</span></> : null}</p>
                      <Files name={job.name} />
                      <History name={job.name} />
                    </td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {data && (
        <section className="card">
          <h2>Switched by environment variable</h2>
          <table>
            <thead><tr><th>Job</th><th>Schedule</th><th>Switch</th><th>Last slot</th></tr></thead>
            <tbody>
              {data.envManaged.map((e) => (
                <tr key={e.name}><td>{e.title}<div className="mono">{e.name}</div></td><td>{describeCron(e.schedule)}</td>
                  <td><span className={`pill ${e.enabled ? 'job-on' : 'job-off'}`}>{e.env}={e.enabled ? '1' : 'off'}</span></td>
                  <td className="mono">{e.lastSlot ?? '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {!data && !err && <p className="loading">Loading…</p>}
    </div>
  );
}
